import { POST_BLACK, POST_COLOR_ROLES, POST_WHITE, contrastRatio, POST_MIN_CONTRAST, type PostColorRole, type PostPalette } from '@nest-recruit/shared';

// ADR 0066 — the colour toolbar's tools: shuffle a matching palette (keeping locked colours), flip light and dark, and
// step back and forward. Pure, so they're tested without a screen.

export type ShuffleStyle = 'shades' | 'pairs' | 'contrast';

export const SHUFFLE_STYLE_LABELS: Readonly<Record<ShuffleStyle, string>> = {
  shades: 'Shades of one colour',
  pairs: 'Matching pairs',
  contrast: 'High contrast',
};

const HUE_TURN = 360;
const COMPLEMENT = 180;
const NEAR_HUE = 30;
const BLACKISH = 0.12;
const WHITEISH = 0.96;

interface Hsl {
  readonly h: number;
  readonly s: number;
  readonly l: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function toHex({ h, s, l }: Hsl): string {
  const hue = ((h % HUE_TURN) + HUE_TURN) % HUE_TURN;
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const x = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - chroma / 2;
  const [r, g, b] =
    hue < 60 ? [chroma, x, 0] : hue < 120 ? [x, chroma, 0] : hue < 180 ? [0, chroma, x] : hue < 240 ? [0, x, chroma] : hue < 300 ? [x, 0, chroma] : [chroma, 0, x];
  const channel = (value: number): string =>
    Math.round(clamp(value + m, 0, 1) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`.toUpperCase();
}

function toHsl(hex: string): Hsl {
  const [r = 0, g = 0, b = 0] = [1, 3, 5].map((start) => Number.parseInt(hex.slice(start, start + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? ((g - b) / d + (g < b ? 6 : 0)) * 60 : max === g ? ((b - r) / d + 2) * 60 : ((r - g) / d + 4) * 60;
  return { h, s, l };
}

/** A new matching palette. Locked roles keep their colour; the rest follow the accent's hue (or a fresh one). */
export function shufflePalette(current: PostPalette, locked: ReadonlySet<PostColorRole>, style: ShuffleStyle, random: () => number): PostPalette {
  const hue = locked.has('accent') ? toHsl(current.accent).h : random() * HUE_TURN;
  const dark = style === 'contrast' && random() < 0.5;
  const fresh: PostPalette = {
    background: dark ? toHex({ h: hue, s: 0.35, l: BLACKISH }) : toHex({ h: hue, s: 0.18, l: WHITEISH }),
    text: dark ? toHex({ h: hue, s: 0.1, l: WHITEISH }) : toHex({ h: hue, s: 0.15, l: BLACKISH }),
    primary: style === 'shades' ? toHex({ h: hue, s: 0.45, l: 0.28 }) : dark ? toHex({ h: hue, s: 0.1, l: WHITEISH }) : toHex({ h: hue, s: 0.2, l: BLACKISH }),
    secondary: style === 'pairs' ? toHex({ h: hue + COMPLEMENT, s: 0.35, l: 0.55 }) : toHex({ h: hue + NEAR_HUE, s: 0.2, l: 0.6 }),
    accent: toHex({ h: hue, s: style === 'shades' ? 0.45 : 0.55, l: style === 'contrast' ? 0.5 : 0.58 }),
  };
  const next = Object.fromEntries(POST_COLOR_ROLES.map((role) => [role, locked.has(role) ? current[role] : fresh[role]]));
  return readable({ ...current, ...next }, locked);
}

/** Light ↔ dark: background and text swap, and the pill follows the text so it still reads. */
export function flipPalette(palette: PostPalette): PostPalette {
  const secondary = toHsl(palette.secondary);
  return readable(
    {
      text: palette.background,
      background: palette.text,
      primary: palette.background,
      secondary: toHex({ ...secondary, l: 1 - secondary.l }),
      accent: palette.accent,
    },
    new Set(),
  );
}

/** Unlocked word colours are nudged to black or white when they wouldn't read on the background. */
function readable(palette: PostPalette, locked: ReadonlySet<PostColorRole>): PostPalette {
  const fix = (role: 'text' | 'primary'): string => {
    if (locked.has(role) || contrastRatio(palette[role], palette.background) >= POST_MIN_CONTRAST) return palette[role];
    return contrastRatio(POST_WHITE, palette.background) >= contrastRatio(POST_BLACK, palette.background) ? POST_WHITE : POST_BLACK;
  };
  return { ...palette, text: fix('text'), primary: fix('primary') };
}

/** Undo and redo for the toolbar: a list of palettes and where we are in it. */
export interface PaletteHistory {
  readonly past: readonly PostPalette[];
  readonly future: readonly PostPalette[];
}

export const EMPTY_HISTORY: PaletteHistory = { past: [], future: [] };
const HISTORY_LIMIT = 50;

export function remember(history: PaletteHistory, before: PostPalette): PaletteHistory {
  return { past: [...history.past, before].slice(-HISTORY_LIMIT), future: [] };
}

export function undo(history: PaletteHistory, current: PostPalette): { readonly history: PaletteHistory; readonly palette: PostPalette } | null {
  const previous = history.past.at(-1);
  if (!previous) return null;
  return { history: { past: history.past.slice(0, -1), future: [current, ...history.future] }, palette: previous };
}

export function redo(history: PaletteHistory, current: PostPalette): { readonly history: PaletteHistory; readonly palette: PostPalette } | null {
  const [next, ...rest] = history.future;
  if (!next) return null;
  return { history: { past: [...history.past, current], future: rest }, palette: next };
}

/** "Text #000000 · Background #F1EFEC · …" for Copy codes. */
export function paletteText(palette: PostPalette, labels: Readonly<Record<PostColorRole, string>>): string {
  return POST_COLOR_ROLES.map((role) => `${labels[role]} ${palette[role]}`).join(' · ');
}
